import { create } from 'zustand';
import {
  listPreDocuments,
  getPreDocument,
  generateClientPreDocument,
  generateDriverPreDocument,
  approvePreDocument,
  rejectPreDocument,
  sendPreDocumentToBsale,
  addDeduction,
  removeDeduction,
  updateLineItem,
  listDriverTariffs,
  createDriverTariff,
  updateDriverTariff,
} from '../api/billing';

const useBillingStore = create((set, get) => ({
  // State
  preDocuments: [],
  preDocumentsCount: 0,
  selectedPreDocument: null,
  filters: {
    type: '',
    status: '',
    companyId: '',
    driverId: '',
    periodStartFrom: '',
    periodStartTo: '',
    limit: 25,
    offset: 0,
  },
  loading: false,
  loadingDetail: false,
  saving: false,
  driverTariffs: [],
  error: null,

  // Filters
  setFilters: (newFilters) =>
    set((s) => ({ filters: { ...s.filters, ...newFilters, offset: 0 } })),

  // Pre-documents list
  fetchPreDocuments: async () => {
    set({ loading: true, error: null });
    try {
      const { filters } = get();
      const params = {};
      if (filters.type) params.type = filters.type;
      if (filters.status) params.status = filters.status;
      if (filters.companyId) params.companyId = filters.companyId;
      if (filters.driverId) params.driverId = filters.driverId;
      if (filters.periodStartFrom) params.periodStartFrom = filters.periodStartFrom;
      if (filters.periodStartTo) params.periodStartTo = filters.periodStartTo;
      params.limit = filters.limit;
      params.offset = filters.offset;

      const { data } = await listPreDocuments(params);
      set({ preDocuments: data.items ?? [], preDocumentsCount: data.count ?? 0 });
    } catch (err) {
      set({ error: err.response?.data?.message ?? err.message });
    } finally {
      set({ loading: false });
    }
  },

  // Pre-document detail
  fetchPreDocument: async (id) => {
    set({ loadingDetail: true, error: null });
    try {
      const { data } = await getPreDocument(id);
      set({ selectedPreDocument: data });
      return data;
    } catch (err) {
      set({ error: err.response?.data?.message ?? err.message });
      throw err;
    } finally {
      set({ loadingDetail: false });
    }
  },

  clearSelectedPreDocument: () => set({ selectedPreDocument: null }),

  // Generate
  generateClientPreDocument: async (data) => {
    set({ saving: true, error: null });
    try {
      const { data: doc } = await generateClientPreDocument(data);
      get().fetchPreDocuments();
      return doc;
    } catch (err) {
      set({ error: err.response?.data?.message ?? err.message });
      throw err;
    } finally {
      set({ saving: false });
    }
  },

  generateDriverPreDocument: async (data) => {
    set({ saving: true, error: null });
    try {
      const { data: doc } = await generateDriverPreDocument(data);
      get().fetchPreDocuments();
      return doc;
    } catch (err) {
      set({ error: err.response?.data?.message ?? err.message });
      throw err;
    } finally {
      set({ saving: false });
    }
  },

  // Edits
  updateLineItem: async (id, idx, netUnitValue) => {
    set({ saving: true, error: null });
    try {
      const { data } = await updateLineItem(id, idx, { netUnitValue });
      set({ selectedPreDocument: data });
      return data;
    } catch (err) {
      set({ error: err.response?.data?.message ?? err.message });
      throw err;
    } finally {
      set({ saving: false });
    }
  },

  addDeduction: async (id, deduction) => {
    set({ saving: true, error: null });
    try {
      const { data } = await addDeduction(id, deduction);
      set({ selectedPreDocument: data });
      return data;
    } catch (err) {
      set({ error: err.response?.data?.message ?? err.message });
      throw err;
    } finally {
      set({ saving: false });
    }
  },

  removeDeduction: async (id, idx) => {
    set({ saving: true, error: null });
    try {
      const { data } = await removeDeduction(id, idx);
      set({ selectedPreDocument: data });
      return data;
    } catch (err) {
      set({ error: err.response?.data?.message ?? err.message });
      throw err;
    } finally {
      set({ saving: false });
    }
  },

  // State transitions
  approve: async (id) => {
    set({ saving: true, error: null });
    try {
      const { data } = await approvePreDocument(id);
      set({ selectedPreDocument: data });
      get().fetchPreDocuments();
      return data;
    } catch (err) {
      set({ error: err.response?.data?.message ?? err.message });
      throw err;
    } finally {
      set({ saving: false });
    }
  },

  reject: async (id, reason) => {
    set({ saving: true, error: null });
    try {
      const { data } = await rejectPreDocument(id, { reason });
      set({ selectedPreDocument: data });
      get().fetchPreDocuments();
      return data;
    } catch (err) {
      set({ error: err.response?.data?.message ?? err.message });
      throw err;
    } finally {
      set({ saving: false });
    }
  },

  sendToBsale: async (id) => {
    set({ saving: true, error: null });
    try {
      const { data } = await sendPreDocumentToBsale(id);
      set({ selectedPreDocument: data });
      get().fetchPreDocuments();
      return data;
    } catch (err) {
      set({ error: err.response?.data?.message ?? err.message });
      throw err;
    } finally {
      set({ saving: false });
    }
  },

  // Driver tariffs
  fetchDriverTariffs: async (driverId) => {
    try {
      const { data } = await listDriverTariffs(driverId);
      set({ driverTariffs: Array.isArray(data) ? data : [] });
    } catch (err) {
      set({ error: err.response?.data?.message ?? err.message });
    }
  },

  createDriverTariff: async (tariff) => {
    set({ saving: true });
    try {
      const { data } = await createDriverTariff(tariff);
      get().fetchDriverTariffs();
      return data;
    } catch (err) {
      set({ error: err.response?.data?.message ?? err.message });
      throw err;
    } finally {
      set({ saving: false });
    }
  },

  updateDriverTariff: async (id, updates) => {
    set({ saving: true });
    try {
      const { data } = await updateDriverTariff(id, updates);
      get().fetchDriverTariffs();
      return data;
    } catch (err) {
      set({ error: err.response?.data?.message ?? err.message });
      throw err;
    } finally {
      set({ saving: false });
    }
  },
}));

export default useBillingStore;
